import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCart, updateCartItemQty, removeCartItem } from "../api/cart";
import { productThumbnailImageUrl } from "../api/product";
import { useAuth } from "../context/AuthContext";
import { useCartSummary } from "../context/CartContext";

function formatPrice(v) {
  return Number(v || 0).toLocaleString() + "원";
}

export default function CartPage() {
  const { auth } = useAuth();
  const { refresh: refreshCartBadge } = useCartSummary();
  const navigate = useNavigate();

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCart(auth?.accessToken);
      setCart(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "장바구니를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const changeQty = async (cartItemId, qty) => {
    if (qty < 1) return;
    await updateCartItemQty(cartItemId, qty, auth?.accessToken);
    await load();
    refreshCartBadge();
  };

  const remove = async (cartItemId) => {
    await removeCartItem(cartItemId, auth?.accessToken);
    await load();
    refreshCartBadge();
  };

  if (loading) return <div className="page"><p className="text-sub">불러오는 중...</p></div>;
  if (error) return <div className="page"><p className="error">{error}</p></div>;

  const items = cart?.items || [];
  const hasUnavailable = items.some((i) => i.unavailable);
  const purchasableCount = items.filter((i) => !i.unavailable).length;

  return (
    <div className="page cart-page">
      <h1 className="page-title">장바구니</h1>

      {items.length === 0 && (
        <div className="empty-state">
          <p className="text-sub">장바구니가 비어 있습니다.</p>
          <Link to="/products" className="link-btn">상품 보러가기</Link>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="cart-item-list">
            {items.map((item) => (
              <div className={`cart-item ${item.unavailable ? "cart-item-unavailable" : ""}`} key={item.cartItemId}>
                <img
                  className="cart-item-thumb"
                  src={productThumbnailImageUrl(item.productId)}
                  alt={item.productName}
                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                />
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.productName}</div>
                  <div className="text-sub">{item.optionName}</div>
                  {item.unavailable ? (
                    <p className="error">{item.unavailableReason}</p>
                  ) : (
                    <div className="cart-item-price">{formatPrice(item.unitPrice)}</div>
                  )}
                </div>
                <div className="qty-stepper">
                  <button type="button" onClick={() => changeQty(item.cartItemId, item.qty - 1)}>-</button>
                  <span>{item.qty}</span>
                  <button type="button" onClick={() => changeQty(item.cartItemId, item.qty + 1)}>+</button>
                </div>
                <div className="cart-item-line-total">{!item.unavailable && formatPrice(item.lineTotal)}</div>
                <button type="button" className="link-btn" onClick={() => remove(item.cartItemId)}>삭제</button>
              </div>
            ))}
          </div>

          {hasUnavailable && (
            <p className="error">품절/판매중지된 상품은 결제에서 제외됩니다. 필요하면 삭제해주세요.</p>
          )}

          <div className="cart-summary">
            <div className="cart-summary-info">
              {cart.orderDiscountAmount > 0 && (
                <>
                  <div className="cart-summary-row text-sub">
                    <span>상품금액</span>
                    <span>{formatPrice(cart.totalAmount)}</span>
                  </div>
                  <div className="cart-summary-row text-sub">
                    <span>주문할인{cart.orderDiscountName ? ` (${cart.orderDiscountName})` : ""}</span>
                    <span>-{formatPrice(cart.orderDiscountAmount)}</span>
                  </div>
                  <p className="text-sub" style={{ fontSize: "0.85em", margin: 0 }}>
                    * 매장이용/픽업 매장 선택 시 할인이 달라질 수 있습니다.
                  </p>
                </>
              )}
              <div className="cart-summary-total">
                결제예정금액 <strong>{formatPrice(cart.orderDiscountAmount > 0 ? cart.finalAmount : cart.totalAmount)}</strong>
              </div>
            </div>
            <button type="button" disabled={purchasableCount === 0} onClick={() => navigate("/checkout")}>
              주문하기
            </button>
          </div>
        </>
      )}
    </div>
  );
}
